// Business-Type Experience Engine , the type registry.
// Design + rationale: docs/expansion-roadmap-2026/33-business-type-experience-engine.md
// The three shipping profiles (standard / catering / bar) and the feature-visibility
// matrix this file encodes: docs/expansion-roadmap-2026/34-business-type-profiles-standard-catering-bar.md
//
// This module is intentionally *not* server-only: the dashboard sidebar (a client
// component) reads presentation profiles from here. It therefore imports OperatingMode
// as a TYPE ONLY (operating-modes.ts is `import 'server-only'`); a type-only import is
// erased at build time and pulls no server runtime into the client bundle.
//
// DESIGN LAW (doc 33): a business type authorizes NOTHING. Billing entitlements and team
// permissions remain the sole authority on what a user may do. A type only *presets*,
// *relabels*, *shows*, and *hides*. It can hide a feature the plan includes; it can never
// reveal a feature the plan excludes.

import type { OperatingMode } from '@/lib/server/operating-modes';
import type { BillingFeatureKey } from '@/lib/billing/plans';
// Value import, and safe: `lib/operating-modes/keys.ts` imports nothing at all ,
// it is the shared key set the server module, the client hook and the build-time
// invariant checker all read. It pulls no server runtime into the client bundle.
import { OPERATING_MODE_LABELS } from '@/lib/operating-modes/keys';
// Type-only: `lib/menu/item-kinds/types.ts` is client-safe and imports nothing.
import type { ItemKind } from '@/lib/menu/item-kinds/types';
// Config-system: source of truth for business type definitions.
import {
  allBusinessTypes as configBusinessTypes,
  allSurfaces as configSurfaces,
  navSurfaces as configNavSurfaces,
  homeActionKeys as configHomeActionKeys,
  navGroups as configNavGroups,
  reportKeys as configReportKeys,
  type BusinessTypeDefinition as ConfigBusinessTypeDefinition,
} from '@/lib/config-system';
// Value import, and safe: order-tracks.json is declarative config with no runtime
// imports, so the client bundle only gains the stage labels it has to render.
// The track DEFINITIONS themselves are read through lib/order-track.ts.
import orderTracksConfig from '@/configs/business-types/order-tracks.json';

export const BUSINESS_TYPE_IDS = ['standard', 'catering'] as const;
export type BusinessTypeId = (typeof BUSINESS_TYPE_IDS)[number];

export const DEFAULT_BUSINESS_TYPE: BusinessTypeId = 'standard';

// ─── Specific sub-types ─────────────────────────────────────────────────────
// Doc 48 §1–2. These used to be cosmetic labels: written to `branches.specific_type`
// by onboarding and never read by anything. Thirty of them, thirteen under
// `standard` alone , a merchant answered two questions and only one was kept.
//
// The cost was concrete. A bakery's business IS the advance custom order, and
// Menuraq already ships every part of it (pickup slots, deposit policies, the
// production prep sheet). But `standard` presets `dine_in + counter_pickup`, so
// `scheduled_pre_order` stayed off, `bookings` stayed hidden, and the bakery
// concluded the product doesn't do pre-orders. Menuraq held the answer AND held
// the question (`specific_type = 'Bakery'`) and never joined them.
//
// A sub-type is now a thin MODIFIER over its parent type: it may override
// `presetModes` and narrow parts of `composition`, and it may nudge vocabulary.
//
// DESIGN LAW (doc 33, unchanged): a sub-type may NEVER touch `surfaces`. It
// presets and relabels; it never grants. The bakery gets its Bookings surface
// because turning `scheduled_pre_order` ON fires that surface's existing reveal
// trigger , still gated by the `scheduled_orders` entitlement, exactly as before.

/**
 * Narrow composition overrides. Deliberately a DEEP partial over `posSetup`
 * rather than doc 48's `Partial<CompositionProfile>`: fast food overrides
 * `archetype` and `hasDiningRoom` but must keep inheriting `skipSteps`, which a
 * shallow partial would force it to restate.
 */
export interface SubTypeCompositionOverride {
  posSetup?: Partial<PosSetupComposition>;
  posEntryWorkspace?: PosWorkspace;
  posWorkspaceLabels?: Partial<Record<PosWorkspace, string>>;
}

export interface SubTypeProfile {
  /** Stored verbatim in `branches.specific_type`; also the picker's label. */
  label: string;
  /** Replaces the parent's presetModes when this sub-type is chosen. */
  presetModes?: OperatingMode[];
  composition?: SubTypeCompositionOverride;
  /** Vocabulary nudges. A juice bar's "menu" is a "drinks list". */
  vocabulary?: Partial<Vocabulary>;
  /**
   * Item kinds this sub-type leads with on top of its parent's. Additive only ,
   * a sub-type may widen the catalog, never narrow it, so a mixed menu on a
   * re-typed branch can never lose the kinds its items already use.
   *
   * The case this exists for: the combo meal IS the fast-food product, and
   * `combo` was reachable only for `ghost_kitchen`, so the one sub-type whose
   * business is built on it could not author one.
   */
  primaryItemKinds?: ItemKind[];
  /**
   * P-09.7a — categories to seed into a NEW menu when this sub-type is chosen,
   * the additive `primaryItemKinds` pattern applied to the catalog skeleton.
   * Seeded at branch/menu creation only, never retroactively, so an existing
   * trading branch's menu is never mutated by a sub-type choice.
   *
   * A Wine Bar and a Nightclub both inherit `standard`'s catalog, which is the
   * problem P-09.7 exists to fix: with these defaults the merchant starts with
   * a skeleton that looks like their business instead of a generic one.
   */
  defaultCategories?: string[];
  /**
   * P-09.7a — how strongly this sub-type's products depend on recipes.
   * `required` for production kitchens that cannot cost/serve without a recipe
   * (bakery, wholesale kitchen), `encouraged` where recipes materially improve
   * costing and allergen coverage, `optional` otherwise. Read-time hint for
   * menu-editor affordances; never enforced.
   */
  recipeExpectation?: 'required' | 'encouraged' | 'optional';
  /**
   * POS lanes this sub-type's business does not use.
   * docs/operational-ux-2026-09/09-phase0-findings.md §0.1
   *
   * NARROWING ONLY, and provably so: `scripts/check-surface-invariants.mjs`
   * fails the build if any id here is not already a lane its PARENT type shows.
   * A sub-type therefore still cannot touch `surfaces` in the sense the design
   * law forbids — it cannot reveal anything, only decline what it was offered.
   *
   * WHY THIS EXISTS. `hiddenWorkspacesFor` could hide `floor` (via the bespoke
   * `typeHasDiningRoom`, which does read the sub-type) but nothing else: every
   * other lane was gated on a TYPE-level `na`. So `standard` says the KDS is
   * `shown`, and Fast Food — a counter and a kitchen in one room, with no screen
   * and no plan to buy one — had no way to say otherwise. It got a Kitchen tab
   * onto a lane it does not own.
   *
   * Prefer the DERIVED narrowing in `lib/business-types/operating-model.ts` where
   * the answer follows from the branch's active modes; `specific_type` is set on
   * roughly 6% of branches, so anything declared here reaches very few of them.
   * This is the second mechanism, not the first.
   */
  hiddenWorkspaces?: PosWorkspace[];
  /**
   * The order track this sub-type's till uses — how the 12 canonical
   * `service_orders.status` values are projected into the stages its staff
   * think in. From `configs/business-types/types.json`
   * `subTypes.<key>.operational.orderTrack`; the definitions live in
   * `configs/business-types/order-tracks.json` and are read by
   * `lib/order-track.ts`.
   *
   * PRESENTATION ONLY. The stored status, the DB CHECK, the KDS predicate and
   * every report keep reading the canonical vocabulary. A track decides which
   * columns the board draws and which single button each order shows — never
   * what is written.
   */
  orderTrack?: string;
  /**
   * How a staff-entered counter sale for this sub-type is fulfilled — the
   * explicit override for `resolveCounterFlow` (lib/pos/counter-flow.ts). From
   * `configs/business-types/types.json`
   * `subTypes.<key>.operational.counterFlow`.
   *
   * OPERATIONAL POLICY, unlike the presentation-only `orderTrack`. Precedence
   * is: `branches.counter_flow` column > this value > the order-track default
   * (quick_service track = pay_and_go). It exists because a sub-type can be a
   * fast counter business (Café has `posEntryWorkspace: counter`) while still
   * carrying no order track of its own, so the track cannot answer this.
   */
  counterFlow?: 'pay_and_go' | 'track_to_handoff';
}

/** A sub-type that genuinely differs by nothing , it just inherits its parent. */
const plain = (label: string): SubTypeProfile => ({ label });

function buildSpecificTypes(): Record<BusinessTypeId, readonly SubTypeProfile[]> {
  const result = {} as Record<BusinessTypeId, readonly SubTypeProfile[]>;
  for (const typeId of BUSINESS_TYPE_IDS) {
    const cfg = configBusinessTypes[typeId];
    if (!cfg?.subTypes) { result[typeId] = []; continue; }
    const profiles: SubTypeProfile[] = [];
    for (const [, subCfg] of Object.entries(cfg.subTypes)) {
      const profile: SubTypeProfile = { label: subCfg.label };
      if (subCfg.presetModes) profile.presetModes = subCfg.presetModes as OperatingMode[];
      if (subCfg.composition) {
        profile.composition = {};
        if (subCfg.composition.posSetup) {
          profile.composition.posSetup = {
            ...subCfg.composition.posSetup,
            skipSteps: (subCfg.composition.posSetup.skipSteps ?? []) as PosSetupStep[],
          } as Partial<PosSetupComposition>;
        }
        if (subCfg.composition.posEntryWorkspace) profile.composition.posEntryWorkspace = subCfg.composition.posEntryWorkspace as PosWorkspace;
        if (subCfg.composition.posWorkspaceLabels) profile.composition.posWorkspaceLabels = subCfg.composition.posWorkspaceLabels as Partial<Record<PosWorkspace, string>>;
      }
      if (subCfg.vocabulary) profile.vocabulary = subCfg.vocabulary as Partial<Vocabulary>;
      if (subCfg.defaultCategories) profile.defaultCategories = subCfg.defaultCategories;
      if (subCfg.recipeExpectation) profile.recipeExpectation = subCfg.recipeExpectation as SubTypeProfile['recipeExpectation'];
      if (subCfg.primaryItemKinds) profile.primaryItemKinds = subCfg.primaryItemKinds as ItemKind[];
      if (subCfg.hiddenWorkspaces) profile.hiddenWorkspaces = subCfg.hiddenWorkspaces as PosWorkspace[];
      if (subCfg.operational?.orderTrack) profile.orderTrack = subCfg.operational.orderTrack;
      if (subCfg.operational?.counterFlow) profile.counterFlow = subCfg.operational.counterFlow;
      profiles.push(profile);
    }
    result[typeId] = profiles;
  }
  return result;
}

export const SPECIFIC_TYPES: Record<BusinessTypeId, readonly SubTypeProfile[]> = buildSpecificTypes();

export function isBusinessTypeId(value: unknown): value is BusinessTypeId {
  return typeof value === 'string' && (BUSINESS_TYPE_IDS as readonly string[]).includes(value);
}

/** Check if a specific type string is valid for the given broad type. */
export function isValidSpecificType(broadTypeId: BusinessTypeId, specificType: string | null | undefined): boolean {
  if (!specificType) return false;
  return SPECIFIC_TYPES[broadTypeId].some((sub) => sub.label === specificType);
}

/**
 * Resolve a stored `specific_type` label to its profile, or null when the branch
 * has none / it doesn't belong to this type (a stale label left behind by a type
 * change must never keep modifying the new type , it silently inherits instead).
 */
export function getSubType(
  typeId: string | null | undefined,
  specificType: string | null | undefined,
): SubTypeProfile | null {
  if (!specificType) return null;
  const type = getBusinessType(typeId);
  return SPECIFIC_TYPES[type.id].find((sub) => sub.label === specificType) ?? null;
}

export const DEFAULT_ORDER_TRACK_ID = orderTracksConfig.defaultTrack;

const ORDER_TRACK_IDS = new Set(Object.keys(orderTracksConfig.tracks));

/**
 * Which order track to draw for this branch. SUB-TYPE FIRST, TYPE SECOND,
 * DEFAULT LAST — the reverse precedence of `lib/business-types/operating-model.ts`,
 * and deliberately so.
 *
 * That module puts modes first precisely because `specific_type` is set on ~6%
 * of branches, so keying the FLOOR SHAPE on it stranded 110 counter-only shops
 * in a full-service layout. A track cannot do that: it changes no capability, no
 * lane and no permission, only which columns the order board draws and which
 * button sits on a card. The worst outcome of a missing sub-type here is the
 * full 6-column board — correct, just busier than it needs to be — so it is safe
 * to let a sub-type, when one is set, win.
 *
 * An unknown id degrades to the default track rather than throwing, matching
 * `getBusinessType` and `normalizePickupApprovalMode`.
 */
export function resolveOrderTrackId(
  typeId: string | null | undefined,
  specificType: string | null | undefined,
): string {
  const subTrack = getSubType(typeId, specificType)?.orderTrack;
  if (subTrack && ORDER_TRACK_IDS.has(subTrack)) return subTrack;
  const type = getBusinessType(typeId);
  const typeTrack = (configBusinessTypes as Record<string, { orderTrack?: string }>)[type.id]?.orderTrack;
  if (typeTrack && ORDER_TRACK_IDS.has(typeTrack)) return typeTrack;
  return DEFAULT_ORDER_TRACK_ID;
}

/**
 * The modes to switch ON when this type + sub-type is selected. Applies at
 * SELECTION TIME only, exactly like `presetModes` already does , never
 * retroactively to a branch already trading. Doc 37 established that changing a
 * branch's type must not silently toggle a mode with live records against it.
 */
export function resolvePresetModes(
  typeId: string | null | undefined,
  specificType?: string | null,
): OperatingMode[] {
  const sub = getSubType(typeId, specificType);
  return [...(sub?.presetModes ?? getBusinessType(typeId).presetModes)];
}

export type CatalogDefaults = {
  defaultCategories: string[];
  recipeExpectation: 'required' | 'encouraged' | 'optional';
};

/**
 * P-09.7a — the catalog defaults a NEW menu should be seeded with for this
 * type + sub-type. Consumed at menu/branch creation only, never retroactively,
 * so a sub-type choice can never mutate an existing trading menu. `standard`'s
 * own default is a neutral skeleton; a sub-type's `defaultCategories` replaces
 * it wholesale (additive would repeat the generic set inside every profile).
 */
export function resolveCatalogDefaults(
  typeId: string | null | undefined,
  specificType?: string | null,
): CatalogDefaults {
  const sub = getSubType(typeId, specificType);
  const defaultCategories = sub?.defaultCategories ?? ['Mains', 'Sides', 'Drinks', 'Desserts'];
  const recipeExpectation = sub?.recipeExpectation ?? 'optional';
  return { defaultCategories, recipeExpectation };
}

/**
 * One-line "here's what this will switch on" for the sub-type pickers. Lives here
 * so the three pickers (onboarding, Settings, BranchModal) share one derivation
 * instead of each growing its own idea of what a sub-type means.
 */
export function subTypePresetHint(
  typeId: string | null | undefined,
  specificType: string | null | undefined,
): string {
  const sub = getSubType(typeId, specificType);
  if (!sub?.presetModes) {
    return 'Helps us describe your business. Uses your type’s standard setup.';
  }
  const names = sub.presetModes.map((mode) => OPERATING_MODE_LABELS[mode]).join(' + ');
  return `Starts this branch with ${names} switched on. You can change this below.`;
}

// ─── Vocabulary ──────────────────────────────────────────────────────────────
// Words the UI substitutes so the same underlying feature reads in the type's language.

export type VocabKey =
  | 'order'
  | 'orders'
  | 'customer'
  | 'customers'
  | 'menu'
  | 'menus'
  | 'session'
  | 'placeOrderCta'
  // The vocabulary had a word for everything the business DOES and no word for
  // what the business IS, or for the person serving. So every screen that needed
  // one wrote "restaurant" and "waiter" , including screens the merchant's own
  // customers read. These two keys are the fix (doc 46 §1).
  /** Lowercase noun for the business itself: "restaurant", "caterer", "bar". */
  | 'business'
  /** Lowercase noun for a front-of-house staff member: "waiter", "bartender". */
  | 'staffMember'
  | 'staffMembers'
  /**
   * Lowercase noun for the thing a guest books in advance on `/book/[menuId]`:
   * "event" for a caterer, "pre-order" for a restaurant, "order" for a bakery.
   *
   * That page was written for catering and hardcoded "event" throughout, but it
   * is the front door for EVERY type that can enable `scheduled_pre_order` , so
   * a bakery taking a birthday-cake order was asked to "book an event".
   */
  | 'advanceBooking'
  | 'advanceBookings'
  /**
   * The full call to action on that page: "Book an event", "Place a pre-order".
   * A whole phrase rather than a verb, because the natural verb and the article
   * both depend on the noun , deriving "Place a/an {noun}" produced "Place an
   * event" for the one type the page was originally written for.
   */
  | 'advanceBookingCta';

export type Vocabulary = Record<VocabKey, string>;

// ─── Nav surfaces & visibility ───────────────────────────────────────────────
// Every nav-relevant surface the engine can show/hide. This enum + the per-type
// visibility map together ARE the feature-visibility matrix in doc 34.

export type NavSurface = (typeof configNavSurfaces)[number];
export const NAV_SURFACES: readonly NavSurface[] = configNavSurfaces as readonly NavSurface[];

// shown    = ● in the type's default presentation.
// optional = ○ hidden by default, revealed when the corresponding mode/feature is on
//              (the mixed-model case: a restaurant that also runs a bar).
// na       = , never surfaced for this type; makes no sense for it, even if the
//              entitlement exists. Distinct from `optional` on purpose.
export type SurfaceVisibility = 'shown' | 'optional' | 'na';

// Reveal triggers moved to `SURFACE_DEFINITIONS` (lib/business-types/surface-definitions.ts)
// so that a surface's reveal, route, permissions and entitlement all live on one row and
// cannot drift apart , see doc 42. The table here used to reveal `tabs` on mode
// `bar_service` with no entitlement check, quietly breaking the doc-33 design law that a
// type may never reveal a feature the plan excludes.

// Which public customer surface is this type's primary entry point.
export type CustomerEntry = 'menu' | 'booking' | 'tab_menu';

// ─── Dashboard home ──────────────────────────────────────────────────────────
// The landing's "Quick actions" the type leads with. A closed key set the profile
// *orders* per type; the page maps each key to a concrete route/icon and gates it by
// permission + entitlement (registry stays route/icon-free, like NAV_SURFACES).
export type HomeActionKey = (typeof configHomeActionKeys)[number];
export const HOME_ACTION_KEYS: readonly HomeActionKey[] = configHomeActionKeys as readonly HomeActionKey[];

export interface DashboardHomeProfile {
  /** Ordered quick actions the landing surfaces first for this type. Filtered at
   *  render by team permission + billing entitlement , this only sets order/choice. */
  quickActions: HomeActionKey[];
  /** Ordered analytics reports this type leads with (P-04.6a). Closed key set ,
   *  exactly like `quickActions`: the registry orders and selects, the analytics
   *  page maps each key to a builder and gates it by permission + entitlement. */
  reports?: ReportKey[];
}

// ─── Report keys ──────────────────────────────────────────────────────────────
// Closed key set for the per-type analytics reports (docs/architecture/
// 04-analytics-collections-and-reporting.md P-04.6). Parallel to HOME_ACTION_KEYS:
// registry stays route/icon-free and only ORDERS per type; the analytics page owns
// the mapping from key -> report builder.
export type ReportKey = (typeof configReportKeys)[number];
export const REPORT_KEYS: readonly ReportKey[] = configReportKeys as readonly ReportKey[];

// ─── Nav group order ─────────────────────────────────────────────────────────
// The sidebar's sections. A closed key set here (route/icon-free, exactly like
// NAV_SURFACES and HOME_ACTION_KEYS); `lib/routing/sidebar-navigation.ts` owns
// the labels and icons and reads the ORDER from here.
//
// This lever exists because the type engine could choose which nav items appear
// and what they were called, but not what the nav LEADS with , the section order
// was one shared constant. So a caterer's dashboard home correctly opened with
// Bookings while its sidebar still opened with the catalog, and the two disagreed
// about what that business does all day.
export type NavGroup = (typeof configNavGroups)[number];
export const NAV_GROUPS: readonly NavGroup[] = configNavGroups as readonly NavGroup[];

export interface PresentationProfile {
  /** Replaces the hard-coded `Restaurant` sidebar group label (DashboardSidebar.tsx). */
  primaryGroupLabel: string;
  vocabulary: Vocabulary;
  surfaces: Record<NavSurface, SurfaceVisibility>;
  customerEntry: CustomerEntry;
  home: DashboardHomeProfile;
  /**
   * Groups this type leads its sidebar with. A PARTIAL order , anything omitted
   * keeps its place in the default sequence behind these. Omit the field
   * entirely to accept the default order unchanged.
   */
  navGroupOrder?: NavGroup[];
}

// ─── Composition ─────────────────────────────────────────────────────────────
// Doc 46 §1. `presentation.surfaces` decides WHETHER a surface is shown; nothing
// decided WHAT a shown surface contains. So every screen below the sidebar
// re-derived its own idea of the business and they disagreed , a caterer was
// asked "Tell us about your restaurant", handed a pre-filled 10-table dining
// room, and dropped onto a walk-up till.
//
// This axis is the single answer to "what is this surface made of for this
// type". Consumers READ it; they never re-derive. Adding a type means filling
// this in, which is what stops a half-built vertical shipping.

/** Steps of the POS setup wizard (`app/pos/[branchId]/setup`). */
export const POS_SETUP_STEPS = ['archetype', 'dining_area', 'counter', 'payments', 'review'] as const;
export type PosSetupStep = (typeof POS_SETUP_STEPS)[number];

/** POS workspaces (`app/pos/[branchId]/<workspace>`). */
export const POS_WORKSPACES = ['floor', 'counter', 'kds_station', 'kitchen_display', 'dispatch', 'qr', 'settings', 'analytics'] as const;
export type PosWorkspace = (typeof POS_WORKSPACES)[number];

export interface PosSetupComposition {
  /**
   * Steps this type must never be asked. The branch already carries the answer,
   * so asking again can only produce a wrong one , the `restaurant_type` enum has
   * no `catering` member, so a caterer forced through the archetype step has to
   * lie about being a full-service restaurant.
   *
   * `archetype` is the one exception to "skip = hide": that step also collects
   * currency and timezone, which every type must still answer, so skipping it
   * removes the archetype QUESTION and leaves the step as plain branch basics.
   */
  skipSteps: PosSetupStep[];
  /**
   * `branch_pos_profile.restaurant_type` to store when `archetype` is skipped.
   * That column predates per-branch business types and its enum does not cover
   * them; this maps a type onto the nearest operational shape it does have.
   */
  archetype: string;
  /** Does this type have a dine-in room at all? Drives table setup and Tables & Zones. */
  hasDiningRoom: boolean;
}

export interface CompositionProfile {
  posSetup: PosSetupComposition;
  /**
   * Where `/pos/[branchId]` lands for this type. Must be a workspace the type can
   * actually reach , a type whose entry is filtered out gets bounced to whatever
   * happened to be first, which is how caterers ended up on the counter till.
   */
  posEntryWorkspace: PosWorkspace;
  /** What a POS workspace is CALLED for this type. Missing key keeps the default. */
  posWorkspaceLabels: Partial<Record<PosWorkspace, string>>;
}

export interface BusinessType {
  id: BusinessTypeId;
  /** Human label for the onboarding picker and Settings. */
  label: string;
  /** One line shown under the label in the onboarding picker. */
  tagline: string;
  /** `coming_soon` types render disabled in the picker (doc 33 open decision #2). */
  status: 'live' | 'coming_soon';
  /** Modes switched ON when this type is selected. Still individually overridable after. */
  presetModes: OperatingMode[];
  /** Upsell hints for onboarding only , NEVER used for enforcement. */
  assumedEntitlements: BillingFeatureKey[];
  presentation: PresentationProfile;
  composition: CompositionProfile;
}

/**
 * The composition for a type that runs a dining room and genuinely varies in
 * shape (full service vs quick service vs cafe) , so the archetype question is
 * a real question and stays.
 */
const DINING_ROOM_COMPOSITION: CompositionProfile = {
  posSetup: { skipSteps: [], archetype: 'full_service', hasDiningRoom: true },
  posEntryWorkspace: 'floor',
  posWorkspaceLabels: {},
};

// ─── Surface-visibility helpers ──────────────────────────────────────────────

/**
 * Derive a surface visibility map for a business type from the surface config's
 * `typeVisibility` field. This eliminates the need to duplicate surface ↔ type
 * visibility data in two places.
 */
function deriveSurfaceMapFromConfig(typeId: BusinessTypeId): Record<NavSurface, SurfaceVisibility> {
  const map = {} as Record<NavSurface, SurfaceVisibility>;
  for (const surface of NAV_SURFACES) {
    const cfg = configSurfaces[surface];
    if (!cfg) { map[surface] = 'na'; continue; }
    const vis = cfg.typeVisibility[typeId] ?? 'na';
    map[surface] = vis as SurfaceVisibility;
  }
  return map;
}

// NOTE (doc 42 D1): surfaces listed here must match their `status` in
// `surface-definitions.ts`. `scripts/check-surface-invariants.mjs` fails the
// build if a surface marked `shown`/`optional` here is still `planned`.

// ─── Config-derived registry ─────────────────────────────────────────────────

function buildBusinessTypes(): Record<BusinessTypeId, BusinessType> {
  const result = {} as Record<BusinessTypeId, BusinessType>;
  for (const typeId of BUSINESS_TYPE_IDS) {
    const cfg = configBusinessTypes[typeId];
    if (!cfg) continue;

    const vocab = (cfg.vocabulary ?? {}) as Vocabulary;
    const comp = cfg.composition;
    const pres = cfg.presentation;

    result[typeId] = {
      id: typeId,
      label: cfg.label,
      tagline: cfg.tagline,
      status: cfg.status as 'live' | 'coming_soon',
      presetModes: cfg.presetModes as OperatingMode[],
      assumedEntitlements: (cfg.assumedEntitlements ?? []) as BillingFeatureKey[],
      presentation: {
        primaryGroupLabel: pres?.primaryGroupLabel ?? cfg.label,
        vocabulary: vocab,
        surfaces: deriveSurfaceMapFromConfig(typeId),
        customerEntry: (pres?.customerEntry ?? 'menu') as CustomerEntry,
        home: {
          quickActions: cfg.quickActions as HomeActionKey[],
          reports: cfg.reports as ReportKey[],
        },
        navGroupOrder: pres?.navGroupOrder as NavGroup[] | undefined,
      },
      composition: comp
        ? {
            posSetup: comp.posSetup as PosSetupComposition,
            posEntryWorkspace: comp.posEntryWorkspace as PosWorkspace,
            posWorkspaceLabels: comp.posWorkspaceLabels as Partial<Record<PosWorkspace, string>>,
          }
        : DINING_ROOM_COMPOSITION,
    };
  }
  return result;
}

export const BUSINESS_TYPES: Record<BusinessTypeId, BusinessType> = buildBusinessTypes();

export const LIVE_BUSINESS_TYPES: BusinessType[] = Object.values(BUSINESS_TYPES).filter(
  (t) => t.status === 'live',
);

// ─── Public API ──────────────────────────────────────────────────────────────

/** Resolve a stored type id to its definition, falling back to the safe default. */
export function getBusinessType(id: string | null | undefined): BusinessType {
  return isBusinessTypeId(id) ? BUSINESS_TYPES[id] : BUSINESS_TYPES[DEFAULT_BUSINESS_TYPE];
}

// `isSurfaceVisible` lived here and answered only a third of the question , it knew about
// type and mode but nothing about permission, entitlement or ownership, so every caller
// bolted its own three gates on top and the four sets of bolts diverged. Ask
// `resolveSurface` (lib/business-types/resolve-surface.ts) instead: it answers the whole
// question in one fixed order and reports WHICH gate closed.

/** The type's word for a concept, e.g. word(type, 'order') → "Booking" for catering. */
export function word(
  typeId: string | null | undefined,
  key: VocabKey,
  specificType?: string | null,
): string {
  const sub = getSubType(typeId, specificType);
  if (sub?.vocabulary?.[key]) return sub.vocabulary[key];
  // Read from config first
  if (isBusinessTypeId(typeId)) {
    const cfgVocab = configBusinessTypes[typeId]?.vocabulary;
    if (cfgVocab?.[key]) return cfgVocab[key];
  }
  return getBusinessType(typeId).presentation.vocabulary[key];
}

/** The type's full vocabulary with any sub-type nudges layered on top. */
export function getVocabulary(
  typeId: string | null | undefined,
  specificType?: string | null,
): Vocabulary {
  // Read from config first
  const cfgVocab = isBusinessTypeId(typeId)
    ? (configBusinessTypes[typeId]?.vocabulary as Vocabulary | undefined)
    : undefined;
  const base = cfgVocab ?? getBusinessType(typeId).presentation.vocabulary;
  const sub = getSubType(typeId, specificType);
  return sub?.vocabulary ? { ...base, ...sub.vocabulary } : base;
}

/**
 * Vocabulary nudges contributed by an *active operating mode* (R31.10). A bar lane
 * in a restaurant should still say "tab", not "table" — the word follows the mode,
 * not the parent type. Layered after the type/sub-type base by `resolveVocabulary`.
 */
export const MODE_VOCABULARY: Partial<Record<OperatingMode, Partial<Vocabulary>>> = {
  bar_service: { session: 'tab', staffMember: 'bartender', staffMembers: 'bartenders' },
  room_folio: { session: 'room', business: 'hotel' },
};

/** Context for `resolveVocabulary` — the same axis set the resolver already carries. */
export interface VocabularyContext {
  branchTypeId: string | null | undefined;
  subTypeId?: string | null;
  activeModes?: readonly OperatingMode[] | null;
  /** The active workstation lane; its modes are the final (narrowest) layer. */
  workstation?: { modes: readonly OperatingMode[] } | null;
}

/**
 * The single vocabulary call (doc 31 R31.10). Layers, in order of increasing
 * specificity: type → sub-type → active mode → workstation lane. The same context
 * object the capability resolver uses, so no component branches on type to pick a
 * word. Pure — no DB, no session.
 */
export function resolveVocabulary(ctx: VocabularyContext): Vocabulary {
  let vocab = getVocabulary(ctx.branchTypeId, ctx.subTypeId);
  for (const mode of ctx.activeModes ?? []) {
    const override = MODE_VOCABULARY[mode];
    if (override) vocab = { ...vocab, ...override };
  }
  for (const mode of ctx.workstation?.modes ?? []) {
    const override = MODE_VOCABULARY[mode];
    if (override) vocab = { ...vocab, ...override };
  }
  return vocab;
}

// ─── Composition accessors (doc 46 §1) ───────────────────────────────────────
// Every consumer asks these; none re-derives "what kind of business is this"
// from a pos_mode, a restaurant_type, or a hardcoded workspace name.

export function getComposition(
  typeId: string | null | undefined,
  specificType?: string | null,
): CompositionProfile {
  // Try config first for the base composition
  const cfgComp = isBusinessTypeId(typeId) ? configBusinessTypes[typeId]?.composition : undefined;
  const base: CompositionProfile = cfgComp
    ? {
        posSetup: cfgComp.posSetup as PosSetupComposition,
        posEntryWorkspace: cfgComp.posEntryWorkspace as PosWorkspace,
        posWorkspaceLabels: cfgComp.posWorkspaceLabels as Partial<Record<PosWorkspace, string>>,
      }
    : BUSINESS_TYPES[isBusinessTypeId(typeId) ? typeId : DEFAULT_BUSINESS_TYPE].composition;

  const override = getSubType(typeId, specificType)?.composition;
  if (!override) return base;
  return {
    posSetup: { ...base.posSetup, ...override.posSetup },
    posEntryWorkspace: override.posEntryWorkspace ?? base.posEntryWorkspace,
    posWorkspaceLabels: { ...base.posWorkspaceLabels, ...override.posWorkspaceLabels },
  };
}

/** Must this type be asked this setup step, or does the branch already know? */
export function posSetupAsksStep(
  typeId: string | null | undefined,
  step: PosSetupStep,
  specificType?: string | null,
): boolean {
  return !getComposition(typeId, specificType).posSetup.skipSteps.includes(step);
}

/** Does this type seat people? Gates table setup, Tables & Zones, dine-in defaults. */
export function typeHasDiningRoom(
  typeId: string | null | undefined,
  specificType?: string | null,
): boolean {
  return getComposition(typeId, specificType).posSetup.hasDiningRoom;
}

/**
 * The workspace `/pos/[branchId]` should open, narrowed to what this branch may
 * actually reach. Falls back to the first reachable workspace, then to `floor`
 * so the caller always has a destination.
 */
export function resolvePosEntryWorkspace(
  typeId: string | null | undefined,
  reachableWorkspaces: readonly string[],
  specificType?: string | null,
): string {
  const preferred = getComposition(typeId, specificType).posEntryWorkspace;
  if (reachableWorkspaces.length === 0) return preferred;
  if (reachableWorkspaces.includes(preferred)) return preferred;
  return reachableWorkspaces[0];
}

/** This type's name for a POS workspace, e.g. `floor` → "Tabs" for a bar. */
export function posWorkspaceLabel(
  typeId: string | null | undefined,
  workspace: PosWorkspace,
  fallback: string,
  specificType?: string | null,
): string {
  return getComposition(typeId, specificType).posWorkspaceLabels[workspace] ?? fallback;
}
